import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Save, Loader2, Eye, EyeOff, RotateCcw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { API } from "./constants";

const FEATURE_META = {
  overview: { icon: "LayoutDashboard", category: "core" },
  accounts: { icon: "Users", category: "core" },
  "follower-growth": { icon: "TrendingUp", category: "analytics" },
  posts: { icon: "Eye", category: "core" },
  "content-growth": { icon: "BarChart3", category: "analytics" },
  comments: { icon: "MessageSquare", category: "analytics" },
  "comment-search": { icon: "Search", category: "analytics" },
  "keyword-search": { icon: "Search", category: "analytics" },
  "competitor-tracking": { icon: "Target", category: "analytics" },
  comparison: { icon: "Globe", category: "analytics" },
  insights: { icon: "Sparkles", category: "analytics" },
  correlation: { icon: "FlaskConical", category: "advanced" },
  spillover: { icon: "Zap", category: "advanced" },
  reports: { icon: "FileText", category: "advanced" },
  guide: { icon: "BookOpen", category: "core" },
  "sync-logs": { icon: "RefreshCw", category: "system" },
};

const FEATURE_LABELS = {
  en: {
    overview: "Dashboard",
    accounts: "Accounts",
    "follower-growth": "Follower Growth",
    posts: "Posts",
    "content-growth": "Content Growth",
    comments: "Comments",
    "comment-search": "Comment Search",
    "keyword-search": "Keyword Search",
    "competitor-tracking": "Competitor Tracking",
    comparison: "Cross-Platform",
    insights: "AI Insights",
    correlation: "Correlation Analysis",
    spillover: "Spillover Analysis",
    reports: "Reports",
    guide: "User Guide",
    "sync-logs": "Sync Logs",
  },
  ko: {
    overview: "대시보드",
    accounts: "계정",
    "follower-growth": "팔로워 추이",
    posts: "게시물",
    "content-growth": "콘텐츠 성장",
    comments: "댓글",
    "comment-search": "댓글 검색",
    "keyword-search": "키워드 검색",
    "competitor-tracking": "경쟁사 트래킹",
    comparison: "크로스플랫폼",
    insights: "AI 인사이트",
    correlation: "상관관계 분석",
    spillover: "스필오버 분석",
    reports: "리포트",
    guide: "사용 가이드",
    "sync-logs": "동기화 로그",
  },
  zh: {
    overview: "仪表板",
    accounts: "账户",
    "follower-growth": "粉丝增长",
    posts: "帖子",
    "content-growth": "内容增长",
    comments: "评论",
    "comment-search": "评论搜索",
    "keyword-search": "关键词搜索",
    "competitor-tracking": "竞品追踪",
    comparison: "跨平台",
    insights: "AI洞察",
    correlation: "相关性分析",
    spillover: "溢出效应分析",
    reports: "报告",
    guide: "用户指南",
    "sync-logs": "同步日志",
  },
};

const CATEGORY_LABELS = {
  en: { core: "Core Features", analytics: "Analytics", advanced: "Advanced Analysis", system: "System" },
  ko: { core: "기본 기능", analytics: "분석", advanced: "고급 분석", system: "시스템" },
  zh: { core: "核心功能", analytics: "分析", advanced: "高级分析", system: "系统" },
};

const PAGE_LABELS = {
  en: {
    title: "Feature Access Control",
    desc: "Control which sidebar features are visible to non-admin users. Admins always have full access.",
    adminNote: "Admin users always see all features regardless of these settings.",
    save: "Save Changes",
    saving: "Saving...",
    reset: "Reset to Defaults",
    enabled: "Visible",
    disabled: "Hidden",
    changed: "Changed",
  },
  ko: {
    title: "기능 접근 제어",
    desc: "일반 사용자에게 표시할 사이드바 기능을 제어합니다. 관리자는 항상 전체 기능에 접근할 수 있습니다.",
    adminNote: "관리자는 이 설정과 관계없이 항상 모든 기능을 볼 수 있습니다.",
    save: "변경사항 저장",
    saving: "저장 중...",
    reset: "기본값으로 초기화",
    enabled: "표시",
    disabled: "숨김",
    changed: "변경됨",
  },
  zh: {
    title: "功能访问控制",
    desc: "控制非管理员用户可以看到的侧边栏功能。管理员始终拥有完全访问权限。",
    adminNote: "管理员用户始终可以看到所有功能，不受这些设置的影响。",
    save: "保存更改",
    saving: "保存中...",
    reset: "恢复默认",
    enabled: "可见",
    disabled: "隐藏",
    changed: "已更改",
  },
};

const FeatureAccessControl = ({ lang = "en" }) => {
  const t = PAGE_LABELS[lang] || PAGE_LABELS.en;
  const featureLabels = FEATURE_LABELS[lang] || FEATURE_LABELS.en;
  const catLabels = CATEGORY_LABELS[lang] || CATEGORY_LABELS.en;

  const [features, setFeatures] = useState({});
  const [originalFeatures, setOriginalFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/settings/feature-access`);
      setFeatures(res.data.features || {});
      setOriginalFeatures(res.data.features || {});
    } catch {
      toast.error("Failed to load feature settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const hasChanges = JSON.stringify(features) !== JSON.stringify(originalFeatures);

  const handleToggle = (featureId) => {
    setFeatures(prev => ({ ...prev, [featureId]: !prev[featureId] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/feature-access`, features);
      setOriginalFeatures({ ...features });
      toast.success(lang === "ko" ? "기능 접근 설정이 저장되었습니다" : lang === "zh" ? "功能权限设置已保存" : "Feature access settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const res = await axios.get(`${API}/settings/feature-access`);
      setFeatures(res.data.features || {});
      setOriginalFeatures(res.data.features || {});
    } catch {
      toast.error("Failed to reset");
    }
  };

  // Group features by category
  const groupedFeatures = {};
  for (const [fid, meta] of Object.entries(FEATURE_META)) {
    const cat = meta.category;
    if (!groupedFeatures[cat]) groupedFeatures[cat] = [];
    groupedFeatures[cat].push(fid);
  }

  const categoryOrder = ["core", "analytics", "advanced", "system"];

  return (
    <div className="space-y-4" data-testid="feature-access-control">
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t.title}
              </CardTitle>
              <CardDescription>{t.desc}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} disabled={!hasChanges} data-testid="reset-features-btn">
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> {t.reset}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!hasChanges || saving} data-testid="save-features-btn">
                {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                {saving ? t.saving : t.save}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Admin Note */}
          <div className="mb-4 rounded-lg bg-primary/5 border border-primary/10 p-3 text-sm text-muted-foreground flex items-start gap-2">
            <Shield className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
            {t.adminNote}
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="space-y-6">
              {categoryOrder.map(cat => {
                const featureIds = groupedFeatures[cat] || [];
                if (featureIds.length === 0) return null;
                return (
                  <div key={cat}>
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                      {catLabels[cat] || cat}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {featureIds.map(fid => {
                        const isEnabled = features[fid] !== undefined ? features[fid] : true;
                        const isChanged = originalFeatures[fid] !== features[fid];
                        return (
                          <div
                            key={fid}
                            className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                              isChanged ? "border-primary/40 bg-primary/5" : "border-border/40"
                            }`}
                            data-testid={`feature-toggle-${fid}`}
                          >
                            <div className="flex items-center gap-3">
                              {isEnabled ? (
                                <Eye className="h-4 w-4 text-primary" />
                              ) : (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              )}
                              <div>
                                <Label className="cursor-pointer text-sm font-medium">
                                  {featureLabels[fid] || fid}
                                </Label>
                                {isChanged && (
                                  <Badge variant="outline" className="ml-2 text-[10px] h-4 text-primary border-primary/30">
                                    {t.changed}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-muted-foreground">
                                {isEnabled ? t.enabled : t.disabled}
                              </span>
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={() => handleToggle(fid)}
                                data-testid={`switch-${fid}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeatureAccessControl;
